import React, { useEffect } from 'react';
import { X, Trash2 } from 'lucide-react';
import type { WidgetConfig, KpiMetric } from '../../store/stockDashboardStore';
import type { Warehouse, StorageArea } from '../../store/warehouseStore';
import type { ScopeMode } from '../../store/stockDashboardStore';
import { useInventoryStore, cataloguePickerItems } from '../../store/inventoryStore';

interface WidgetConfigPanelProps {
  widget: WidgetConfig;
  isDark: boolean;
  scopeMode: ScopeMode;
  warehouses: Warehouse[];
  storageAreasByWarehouse: Record<string, StorageArea[]>;
  onClose: () => void;
  onUpdate: (patch: Partial<WidgetConfig>) => void;
  onRemove: () => void;
}

const ACCENT_SWATCHES = [
  { label: 'None',    value: '' },
  { label: 'Blue',    value: '#3b82f6' },
  { label: 'Amber',   value: '#f59e0b' },
  { label: 'Orange',  value: '#f97316' },
  { label: 'Emerald', value: '#10b981' },
  { label: 'Red',     value: '#ef4444' },
  { label: 'Purple',  value: '#8b5cf6' },
  { label: 'Teal',    value: '#14b8a6' },
  { label: 'Pink',    value: '#ec4899' },
  { label: 'Indigo',  value: '#6366f1' },
  { label: 'Gray',    value: '#6b7280' },
];

const BG_TINT_SWATCHES = [
  { label: 'None',    value: 'transparent' },  // truly transparent — shows app bg
  { label: 'Grey',    value: '' },              // default widget background colour
  { label: 'Blue',    value: '#1e40af18' },
  { label: 'Amber',   value: '#d9770618' },
  { label: 'Emerald', value: '#06653118' },
  { label: 'Red',     value: '#b9191918' },
  { label: 'Purple',  value: '#6d28d918' },
  { label: 'Teal',    value: '#0d737618' },
];

const KPI_METRICS: { value: KpiMetric; label: string }[] = [
  { value: 'total_items',       label: 'Total Items' },
  { value: 'checked_out',       label: 'Checked Out' },
  { value: 'pending_requests',  label: 'Pending Requests' },
  { value: 'inventory_value',   label: 'Inventory Value' },
];

const WidgetConfigPanel: React.FC<WidgetConfigPanelProps> = ({
  widget,
  isDark,
  scopeMode,
  warehouses,
  storageAreasByWarehouse,
  onClose,
  onUpdate,
  onRemove,
}) => {
  const bg     = isDark ? '#20242b' : '#ffffff';
  const border = isDark ? '#343a43' : '#e5e7eb';
  const text   = isDark ? '#f3f4f6' : '#111827';
  const muted  = isDark ? '#9ca3af' : '#6b7280';
  const inputBg = isDark ? '#171a1f' : '#f9fafb';
  const perWidgetEnabled = scopeMode === 'per_widget';
  const selectedScopeType = widget.scopeType ?? 'inherit';
  const selectedWarehouseId = widget.scopeWarehouseId ?? warehouses[0]?.id ?? null;
  const availableAreas = selectedWarehouseId ? (storageAreasByWarehouse[selectedWarehouseId] ?? []) : [];

  const { items, fetchItems } = useInventoryStore();
  useEffect(() => {
    if (widget.type === 'stock_by_item' || widget.type === 'stock_by_item_counts') fetchItems();
  }, [widget.type, fetchItems]);

  return (
    <div
      className="absolute top-0 right-0 bottom-0 w-60 flex flex-col shadow-2xl z-30 overflow-y-auto"
      style={{ backgroundColor: bg, borderLeft: `1px solid ${border}` }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 flex-shrink-0"
        style={{ borderBottom: `1px solid ${border}` }}
      >
        <span className="text-sm font-semibold" style={{ color: text }}>Widget Settings</span>
        <button
          onClick={onClose}
          className="p-0.5 rounded hover:opacity-70 transition-opacity"
          style={{ color: muted }}
        >
          <X size={15} />
        </button>
      </div>

      <div className="flex-1 p-4 space-y-5 overflow-y-auto">
        {/* Title */}
        <div>
          <label className="block text-xs font-medium mb-1.5" style={{ color: muted }}>Title</label>
          <input
            type="text"
            value={widget.title}
            onChange={(e) => onUpdate({ title: e.target.value })}
            className="w-full px-2.5 py-1.5 text-sm rounded border focus:outline-none focus:ring-1 focus:ring-blue-500"
            style={{ backgroundColor: inputBg, borderColor: border, color: text }}
          />
        </div>

        {/* KPI Metric selector */}
        {widget.type === 'kpi' && (
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: muted }}>Metric</label>
            <select
              value={widget.kpiMetric}
              onChange={(e) => onUpdate({ kpiMetric: e.target.value as KpiMetric })}
              className="w-full px-2.5 py-1.5 text-sm rounded border focus:outline-none focus:ring-1 focus:ring-blue-500"
              style={{ backgroundColor: inputBg, borderColor: border, color: text }}
            >
              {KPI_METRICS.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
        )}

        {/* Item-based widgets — catalogue item */}
        {(widget.type === 'stock_by_item' || widget.type === 'stock_by_item_counts') && (
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: muted }}>Item type</label>
            <select
              value={widget.stockCatalogueItemId ?? ''}
              onChange={(e) => onUpdate({ stockCatalogueItemId: e.target.value || null })}
              className="w-full px-2.5 py-1.5 text-sm rounded border focus:outline-none focus:ring-1 focus:ring-blue-500"
              style={{ backgroundColor: inputBg, borderColor: border, color: text }}
            >
              <option value="">Select item…</option>
              {cataloguePickerItems(items, widget.stockCatalogueItemId).map(i => (
                <option key={i.id} value={i.id}>{i.name}{i.sku ? ` · ${i.sku}` : ''}{i.isDiscontinued ? ' (discontinued)' : ''}</option>
              ))}
            </select>
            <p className="text-[10px] mt-1.5 leading-snug" style={{ color: muted }}>
              Counts respect the widget scope (default warehouse or per-widget scope).
            </p>
          </div>
        )}

        {/* Accent / left bar color */}
        <div>
          <label className="block text-xs font-medium mb-2" style={{ color: muted }}>Accent Color</label>
          <div className="flex flex-wrap gap-2">
            {ACCENT_SWATCHES.map(s => {
              const isNone     = s.value === '';
              const isSelected = widget.accentColor === s.value;
              return (
                <button
                  key={s.label}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={() => onUpdate({ accentColor: s.value })}
                  title={s.label}
                  className="w-7 h-7 rounded-full transition-all flex items-center justify-center overflow-hidden"
                  style={{
                    background: isNone
                      ? `repeating-conic-gradient(#aaa 0% 25%, #fff 0% 50%) 0 0 / 8px 8px`
                      : s.value,
                    outline: isSelected ? `2px solid ${text}` : '2px solid transparent',
                    outlineOffset: '2px',
                  }}
                />
              );
            })}
          </div>
        </div>

        {/* Background tint */}
        <div>
          <label className="block text-xs font-medium mb-2" style={{ color: muted }}>Background Tint</label>
          <div className="flex flex-wrap gap-2">
            {BG_TINT_SWATCHES.map(s => {
              const isTransparent = s.value === 'transparent';
              const isSelected = widget.bgTint === s.value;
              return (
                <button
                  key={s.label}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={() => onUpdate({ bgTint: s.value })}
                  title={s.label}
                  className="w-7 h-7 rounded flex items-center justify-center text-[9px] font-medium transition-all overflow-hidden"
                  style={{
                    // Transparent swatch: checkerboard pattern
                    background: isTransparent
                      ? `repeating-conic-gradient(#aaa 0% 25%, #fff 0% 50%) 0 0 / 8px 8px`
                      : (s.value || (isDark ? '#2b3140' : '#f3f4f6')),
                    outline: isSelected ? `2px solid ${text}` : `1px solid ${border}`,
                    outlineOffset: '1px',
                    color: muted,
                  }}
                />
              );
            })}
          </div>
        </div>

        {/* Widget scope (only active in Per-widget Scope mode) */}
        <div>
          <label className="block text-xs font-medium mb-2" style={{ color: muted }}>Scope</label>
          <div
            className="space-y-2 rounded border p-2.5"
            style={{
              borderColor: border,
              opacity: perWidgetEnabled ? 1 : 0.55,
              backgroundColor: perWidgetEnabled ? 'transparent' : (isDark ? '#171a1f88' : '#f3f4f688'),
            }}
          >
            {!perWidgetEnabled && (
              <p className="text-[11px] mb-1" style={{ color: muted }}>
                Enable "Per-widget Scope" in Widget Scope to customize this widget.
              </p>
            )}

            <label className="flex items-center gap-2 text-xs" style={{ color: text }}>
              <input
                type="radio"
                name="widget-scope-type"
                checked={selectedScopeType === 'inherit'}
                disabled={!perWidgetEnabled}
                onChange={() => onUpdate({ scopeType: 'inherit', scopeWarehouseId: null, scopeStorageAreaId: null })}
              />
              Inherit Global Scope
            </label>

            <label className="flex items-center gap-2 text-xs" style={{ color: text }}>
              <input
                type="radio"
                name="widget-scope-type"
                checked={selectedScopeType === 'warehouse'}
                disabled={!perWidgetEnabled}
                onChange={() => onUpdate({ scopeType: 'warehouse', scopeStorageAreaId: null })}
              />
              Warehouse
            </label>

            {selectedScopeType === 'warehouse' && (
              <select
                disabled={!perWidgetEnabled}
                value={selectedWarehouseId ?? ''}
                onChange={(e) => onUpdate({ scopeWarehouseId: e.target.value || null, scopeStorageAreaId: null })}
                className="w-full px-2 py-1.5 text-xs rounded border focus:outline-none focus:ring-1 focus:ring-blue-500"
                style={{ backgroundColor: inputBg, borderColor: border, color: text }}
              >
                {warehouses.map(w => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
            )}

            <label className="flex items-center gap-2 text-xs" style={{ color: text }}>
              <input
                type="radio"
                name="widget-scope-type"
                checked={selectedScopeType === 'storage_area'}
                disabled={!perWidgetEnabled}
                onChange={() => onUpdate({ scopeType: 'storage_area' })}
              />
              Storage Area
            </label>

            {selectedScopeType === 'storage_area' && (
              <div className="space-y-1.5">
                <select
                  disabled={!perWidgetEnabled}
                  value={selectedWarehouseId ?? ''}
                  onChange={(e) => onUpdate({ scopeWarehouseId: e.target.value || null, scopeStorageAreaId: null })}
                  className="w-full px-2 py-1.5 text-xs rounded border focus:outline-none focus:ring-1 focus:ring-blue-500"
                  style={{ backgroundColor: inputBg, borderColor: border, color: text }}
                >
                  {warehouses.map(w => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
                <select
                  disabled={!perWidgetEnabled || availableAreas.length === 0}
                  value={widget.scopeStorageAreaId ?? ''}
                  onChange={(e) => onUpdate({ scopeStorageAreaId: e.target.value || null })}
                  className="w-full px-2 py-1.5 text-xs rounded border focus:outline-none focus:ring-1 focus:ring-blue-500"
                  style={{ backgroundColor: inputBg, borderColor: border, color: text }}
                >
                  <option value="">Select storage area</option>
                  {availableAreas.map(area => (
                    <option key={area.id} value={area.id}>{area.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer – remove button */}
      <div className="p-4 flex-shrink-0" style={{ borderTop: `1px solid ${border}` }}>
        <button
          onMouseDown={(e) => e.stopPropagation()}
          onClick={onRemove}
          className="w-full flex items-center justify-center gap-2 text-xs px-3 py-2 rounded transition-colors text-red-400 hover:bg-red-500/10"
          style={{ border: `1px solid rgba(239,68,68,0.3)` }}
        >
          <Trash2 size={12} />
          Remove Widget
        </button>
      </div>
    </div>
  );
};

export default WidgetConfigPanel;
